// Recruiting — the candidate table. One table scale (`table.data`, the bare
// `<table>`/`th`/`td` rules in styles/global.css — see designlaw.test.ts):
// first column left, every other cell centred, nothing styled here.
import type { JSX } from 'preact';
import { useTableSort } from '../../lib/tableSort';
import { SortableTh } from '../SortableTh';
import { EmptyState } from '../FaultState';
import { OUTCOME_LABEL } from './actions/constants';
import {
  formatCivilDate, STAGE_LABEL, FLAG_LABEL,
  type CandidateRow,
} from '../../lib/recruitingApi';

const EM_DASH = '—';

function outcomeText(code: string): string {
  return (OUTCOME_LABEL as Record<string, string>)[code] ?? code;
}

function FlagChips({ row }: { row: CandidateRow }): JSX.Element | null {
  if (row.flags.length === 0) return null;
  return (
    <>
      {row.flags.map((f) => (
        <span key={f} class={f === 'DO_NOT_CONTACT' ? 'badge bad' : f === 'IN_PROCESS' ? 'badge ok' : 'badge'}>
          {FLAG_LABEL[f] ?? f}
        </span>
      ))}
    </>
  );
}

export function CandidateTable(
  {
    rows, total, showing, archive, searching = false, loading, emptyLabel, selectedId, onSelect,
  }: {
    rows: CandidateRow[];
    total: number;
    showing: number;
    archive: boolean;
    /** Global name search: adds the clinic and shows stage plus outcome. */
    searching?: boolean;
    loading: boolean;
    emptyLabel: string;
    selectedId: number | null;
    onSelect: (id: number) => void;
  },
): JSX.Element {
  const sorted = useTableSort(rows, {
    name: (r) => `${r.lastName}, ${r.firstName}`,
    position: (r) => r.position,
    stage: (r) => (archive ? r.outcome : STAGE_LABEL[r.stage] ?? r.stage),
    since: (r) => (archive && !searching ? r.outcomeAt : r.stageSince),
    lastTouch: (r) => r.lastTouchAt,
    source: (r) => r.source,
    received: (r) => r.receivedAt,
  });

  return (
    <div data-testid="recruiting-table-wrap">
      <p class="muted" data-testid="recruiting-table-count">
        {total} total · showing {showing}
      </p>
      {loading ? (
        <EmptyState label="Loading candidates…" />
      ) : rows.length === 0 ? (
        <EmptyState label={emptyLabel} />
      ) : (
        <div class="table-wrap">
          <table class="data" data-testid="recruiting-table">
            <thead>
              <tr>
                <SortableTh label="Candidate" sortKey="name" state={sorted.state} onSort={sorted.toggle} className="name" />
                {searching ? <th>Clinic</th> : null}
                <SortableTh label="Position" sortKey="position" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label={archive ? 'Outcome' : 'Stage'} sortKey="stage" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Since" sortKey="since" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Last touch" sortKey="lastTouch" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Source" sortKey="source" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Received" sortKey="received" state={sorted.state} onSort={sorted.toggle} />
                <th>Also applied</th>
                <th>Resume</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {sorted.rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`recruiting-row-${r.id}`}
                  class={['rowlink', r.doNotContact ? 'dnc' : null, r.id === selectedId ? 'sel' : null].filter(Boolean).join(' ')}
                  onClick={() => onSelect(r.id)}
                >
                  <td class="name">
                    {/* A real link to the person's page, so it reads as one
                        and opens in a new tab if asked; a plain click opens
                        it in place (owner, 2026-09-25). */}
                    <a
                      class="candidate-link"
                      href={`/recruiting?candidate=${r.id}`}
                      data-testid={`recruiting-name-${r.id}`}
                      onClick={(e: MouseEvent) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        e.stopPropagation();
                        onSelect(r.id);
                      }}
                    >
                      {r.lastName}, {r.firstName}
                    </a>
                  </td>
                  {searching ? <td>{r.homeLocationCode || EM_DASH}</td> : null}
                  <td>{r.position || EM_DASH}</td>
                  <td>
                    {searching && r.outcome ? (
                      <span class="badge bad" data-testid={`recruiting-closed-${r.id}`}>
                        Closed — {outcomeText(r.outcome)}
                      </span>
                    ) : (
                      <span class="badge">{archive ? (r.outcome || EM_DASH) : (STAGE_LABEL[r.stage] ?? r.stage)}</span>
                    )}
                    {searching && r.doNotContact ? (
                      <span class="badge bad" data-testid={`recruiting-dnc-${r.id}`}>Do not contact</span>
                    ) : null}
                    {searching && (r.flags.includes('CURRENT_EMPLOYEE') || r.flags.includes('OLD_EMPLOYEE')) ? (
                      <span class="badge" data-testid={`recruiting-emp-${r.id}`}>
                        {r.flags.includes('CURRENT_EMPLOYEE') ? 'Current employee' : 'Old employee'}
                      </span>
                    ) : null}
                  </td>
                  <td>{formatCivilDate(archive && !searching ? r.outcomeAt : r.stageSince)}</td>
                  <td>{formatCivilDate(r.lastTouchAt)}</td>
                  <td>{r.source || EM_DASH}</td>
                  <td>{formatCivilDate(r.receivedAt)}</td>
                  <td>{r.alsoApplied.length > 0 ? r.alsoApplied.join(', ') : EM_DASH}</td>
                  <td data-testid={`recruiting-resume-cell-${r.id}`}>
                    {/* The current resume when one is marked, else the newest
                        (owner, 2026-10-11; server resumeOrderSql). */}
                    {r.resumeUrl ? (
                      <a
                        class="doc-link" href={r.resumeUrl} target="_blank" rel="noreferrer"
                        title={r.resumeName ? `${r.resumeName}${r.resumeIsCurrent ? ' (current)' : ''}` : undefined}
                        onClick={(e: MouseEvent) => e.stopPropagation()}
                      >
                        {r.resumeIsCurrent ? 'Open (current)' : 'Open'}
                      </a>
                    ) : r.hasFile ? 'Yes' : <span class="warn">Missing</span>}
                  </td>
                  <td><FlagChips row={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
